<template>
  <v-container fluid class="pa-6 recruitment-access-page">
    <v-card class="assignment-hero mb-6" dark flat>
      <v-card-text class="py-6 px-8">
        <div class="d-flex justify-space-between align-center flex-wrap">
          <div class="mb-3 mb-md-0">
            <div class="caption text-uppercase font-weight-bold">
              {{ activeTab === 0 ? 'Reclutamiento profesional' : 'Reclutamiento masivo' }}
            </div>
            <h1 class="text-h4 font-weight-bold mb-2">Permisos y asignaciones</h1>
            <p class="mb-0 assignment-subtitle">
              {{ activeTab === 0
                ? 'Marca usuarios como administradores del módulo y asigna procesos profesionales a los responsables correctos.'
                : 'Asigna requerimientos masivos aprobados a los responsables correspondientes.' }}
            </p>
          </div>
          <v-chip color="white" text-color="primary" large>
            <v-icon left small>{{ activeTab === 0 ? 'mdi-briefcase-account-outline' : 'mdi-account-group-outline' }}</v-icon>
            {{ activeTab === 0 ? `${processOptions.length} procesos` : `${massiveProcesses.length} requerimientos` }} visibles
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <v-tabs v-model="activeTab" class="mb-6 elevation-1 rounded-lg" background-color="white" color="primary" grow>
      <v-tab>
        <v-icon left>mdi-briefcase-account-outline</v-icon>
        Reclutamiento Profesional
      </v-tab>
      <v-tab>
        <v-icon left>mdi-account-group-outline</v-icon>
        Reclutamiento Masivo
      </v-tab>
    </v-tabs>

    <!-- TAB 1: RECLUTAMIENTO PROFESIONAL -->
    <v-row dense v-if="activeTab === 0">
      <!-- Columna Izquierda: Procesos (Convocatorias) -->
      <v-col cols="12" lg="7">
        <v-card outlined class="pa-4 mb-4 recruitment-card elevation-1">
          <div class="d-flex justify-space-between align-center flex-wrap mb-4">
            <div>
              <div class="text-h6 font-weight-bold">Convocatorias / Procesos</div>
              <div class="text-caption grey--text text--darken-1">
                Haz clic en una fila para inspeccionar/asignar responsables, o marca las casillas para asignación masiva.
              </div>
            </div>
            <v-btn small text color="primary" @click="reloadProcesses" :loading="loadingProcesses">
              <v-icon left small>mdi-refresh</v-icon>
              Recargar procesos
            </v-btn>
          </div>

          <v-text-field v-model="searchProcess" append-icon="mdi-magnify" label="Buscar por ID, código o puesto..."
            single-line hide-details outlined dense class="mb-4" clearable />

          <v-data-table v-model="selectedProcesses" :headers="processHeaders" :items="processes" :search="searchProcess"
            show-select item-key="RprID" :loading="loadingProcesses" class="elevation-0 recruitment-table"
            :item-class="getProcessRowClass" @click:row="onProcessRowClick" :footer-props="{
              itemsPerPageOptions: [10, 25, 50, 100],
              showCurrentPage: true,
              showFirstLastPage: true
            }" :items-per-page="10" dense>
            <template v-slot:item.RprID="{ item }">
              <span class="font-weight-bold grey--text text--darken-2">{{ item.RprID }}</span>
            </template>
            <template v-slot:item.ReqID="{ item }">
              <v-chip x-small outlined label color="primary">{{ item.ReqID ? `REQ-${item.ReqID}` : '-' }}</v-chip>
            </template>
            <template v-slot:item.RequestedPosition="{ item }">
              <span class="font-weight-medium text-body-2">{{ item.ReqName || item.RequestedPosition || "Proceso" }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Columna Derecha: Usuarios (Responsables) -->
      <v-col cols="12" lg="5">
        <v-card outlined class="pa-4 mb-4 user-card elevation-1">
          <div class="d-flex justify-space-between align-center flex-wrap mb-4">
            <div>
              <div class="text-h6 font-weight-bold">Responsables / Usuarios</div>
              <div class="text-caption grey--text text--darken-1">
                Selecciona a los usuarios que tendrán acceso.
              </div>
            </div>
          </div>

          <v-text-field v-model="searchUser" append-icon="mdi-magnify" label="Buscar por usuario o nombre..."
            single-line hide-details outlined dense class="mb-4" clearable />

          <v-data-table v-model="selectedUsers" :headers="userHeaders" :items="availableUsers" :search="searchUser"
            show-select item-key="UsrID" class="elevation-0 user-table" :item-class="getUserRowClass"
            @click:row="onUserRowClick" :footer-props="{
              itemsPerPageOptions: [10, 25, 50, -1],
              showCurrentPage: true,
              showFirstLastPage: true
            }" :items-per-page="10" dense>
            <template v-slot:item.UsrName="{ item }">
              <span class="font-weight-bold text-body-2">{{ item.UsrName }}</span>
            </template>
            <template v-slot:item.FullName="{ item }">
              <span class="text-caption grey--text text--darken-2">{{ item.FullName || item.NtpFullName }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Fila de Estado y Botón de Acción -->
      <v-col cols="12">
        <v-card v-if="selectedProcesses.length > 0 || selectedRprID" class="pa-4 assignment-status-card elevation-1" outlined>
          <div class="d-flex justify-space-between align-center flex-wrap">
            <div class="mb-3 mb-sm-0">
              <div v-if="selectedProcesses.length > 1" class="d-flex align-center">
                <v-icon color="orange darken-2" class="mr-3" large>mdi-alert-decagram-outline</v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-bold orange--text text--darken-3">
                    Modo Asignación Masiva Activo
                  </div>
                  <div class="text-body-2 mb-0 grey--text text--darken-3">
                    Se asignarán los <strong>{{ selectedUsers.length }}</strong> usuarios seleccionados a los <strong>{{ selectedProcesses.length }}</strong> procesos profesionales seleccionados.
                  </div>
                </div>
              </div>
              <div v-else class="d-flex align-center">
                <v-icon color="success" class="mr-3" large>mdi-check-decagram-outline</v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-bold success--text">
                    Asignación Simple
                  </div>
                  <div class="text-body-2 mb-0 grey--text text--darken-3">
                    Proceso activo: <span class="font-weight-bold">{{ activeProcessText }}</span>.
                    Usuarios asignados: <strong>{{ selectedUsers.length }}</strong>.
                  </div>
                </div>
              </div>
            </div>
            <div>
              <v-btn v-if="selectedProcesses.length > 1" color="orange darken-2" dark large :loading="savingAssignments"
                @click="saveAssignments">
                <v-icon left>mdi-content-save-all-outline</v-icon>
                Guardar Asignación Masiva
              </v-btn>
              <v-btn v-else color="success" large :loading="savingAssignments" :disabled="!selectedRprID"
                @click="saveAssignments">
                <v-icon left>mdi-content-save-outline</v-icon>
                Guardar Asignación
              </v-btn>
            </div>
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="!selectedRprID && !selectedUsrID && selectedProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">Resumen</div>
          <div class="text-body-2 grey--text">
            Haz clic en una convocatoria para ver sus responsables asignados, o haz clic en un responsable para ver sus convocatorias asignadas.
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="selectedRprID && selectedProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">
            Resumen de asignaciones para: {{ activeProcessText }}
          </div>
          <div v-if="processUserList.length === 0" class="text-body-2 grey--text">
            No hay usuarios asignados a este proceso.
          </div>
          <div v-else class="d-flex flex-wrap">
            <v-chip v-for="user in processUserList" :key="user.UsrID" small label class="ma-1" color="blue lighten-5"
              text-color="blue darken-3">
              <v-icon left small>mdi-account</v-icon>
              {{ user.UsrName }} - {{ user.FullName || user.NtpFullName }}
            </v-chip>
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="selectedUsrID && !selectedRprID && selectedProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">
            Procesos asignados a: {{ activeUserText }}
          </div>
          <div v-if="userProcessList.length === 0" class="text-body-2 grey--text">
            Este usuario no tiene procesos asignados.
          </div>
          <div v-else class="d-flex flex-wrap">
            <v-chip v-for="proc in userProcessList" :key="proc.RprID" small label class="ma-1" color="orange lighten-5"
              text-color="orange darken-4" @click="onProcessRowClick(proc)">
              <v-icon left small>mdi-briefcase-outline</v-icon>
              {{ proc.RprID }} - {{ proc.ReqName || proc.RequestedPosition || 'Proceso' }}
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- TAB 2: RECLUTAMIENTO MASIVO -->
    <v-row dense v-if="activeTab === 1">
      <!-- Columna Izquierda: Requerimientos Masivos Aprobados -->
      <v-col cols="12" lg="7">
        <v-card outlined class="pa-4 mb-4 recruitment-card elevation-1">
          <div class="d-flex justify-space-between align-center flex-wrap mb-4">
            <div>
              <div class="text-h6 font-weight-bold">Requerimientos Masivos Aprobados</div>
              <div class="text-caption grey--text text--darken-1">
                Haz clic en un requerimiento para ver/asignar responsables, o marca varios para asignación masiva.
              </div>
            </div>
            <v-btn small text color="primary" @click="loadMassiveApproved" :loading="loadingMassiveProcesses">
              <v-icon left small>mdi-refresh</v-icon>
              Recargar
            </v-btn>
          </div>

          <v-text-field v-model="searchMassiveProcess" append-icon="mdi-magnify" label="Buscar por ID, campaña o puesto..."
            single-line hide-details outlined dense class="mb-4" clearable />

          <v-data-table v-model="selectedMassiveProcesses" :headers="massiveProcessHeaders" :items="massiveProcesses"
            :search="searchMassiveProcess" show-select item-key="ReqID" :loading="loadingMassiveProcesses"
            class="elevation-0 recruitment-table" :item-class="getMassiveRowClass" @click:row="onMassiveRowClick" :footer-props="{
              itemsPerPageOptions: [10, 25, 50, 100],
              showCurrentPage: true,
              showFirstLastPage: true
            }" :items-per-page="10" dense>
            <template v-slot:item.ReqID="{ item }">
              <span class="font-weight-bold grey--text text--darken-2">REQ-{{ item.ReqID }}</span>
            </template>
            <template v-slot:item.CmpName="{ item }">
              <span class="font-weight-medium text-body-2">{{ item.CmpName }}</span>
            </template>
            <template v-slot:item.PstName="{ item }">
              <span class="text-body-2">{{ item.PstName }}</span>
            </template>
            <template v-slot:item.ReqQuantity="{ item }">
              <v-chip x-small color="teal" text-color="white" class="font-weight-bold">{{ item.ReqQuantity }} vacantes</v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Columna Derecha: Usuarios (Responsables) -->
      <v-col cols="12" lg="5">
        <v-card outlined class="pa-4 mb-4 user-card elevation-1">
          <div class="d-flex justify-space-between align-center flex-wrap mb-4">
            <div>
              <div class="text-h6 font-weight-bold">Responsables / Usuarios</div>
              <div class="text-caption grey--text text--darken-1">
                Selecciona a los usuarios que tendrán acceso.
              </div>
            </div>
          </div>

          <v-text-field v-model="searchMassiveUser" append-icon="mdi-magnify" label="Buscar por usuario o nombre..."
            single-line hide-details outlined dense class="mb-4" clearable />

          <v-data-table v-model="selectedMassiveUsers" :headers="userHeaders" :items="availableUsers"
            :search="searchMassiveUser" show-select item-key="UsrID" class="elevation-0 user-table"
            :item-class="getMassiveUserRowClass" @click:row="onMassiveUserRowClick" :footer-props="{
              itemsPerPageOptions: [10, 25, 50, -1],
              showCurrentPage: true,
              showFirstLastPage: true
            }" :items-per-page="10" dense>
            <template v-slot:item.UsrName="{ item }">
              <span class="font-weight-bold text-body-2">{{ item.UsrName }}</span>
            </template>
            <template v-slot:item.FullName="{ item }">
              <span class="text-caption grey--text text--darken-2">{{ item.FullName || item.NtpFullName }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Fila de Estado y Botón de Acción -->
      <v-col cols="12">
        <v-card v-if="selectedMassiveProcesses.length > 0 || selectedMassiveReqID" class="pa-4 assignment-status-card elevation-1" outlined>
          <div class="d-flex justify-space-between align-center flex-wrap">
            <div class="mb-3 mb-sm-0">
              <div v-if="selectedMassiveProcesses.length > 1" class="d-flex align-center">
                <v-icon color="orange darken-2" class="mr-3" large>mdi-alert-decagram-outline</v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-bold orange--text text--darken-3">
                    Modo Asignación Masiva Activo
                  </div>
                  <div class="text-body-2 mb-0 grey--text text--darken-3">
                    Se asignarán los <strong>{{ selectedMassiveUsers.length }}</strong> usuarios seleccionados a los <strong>{{ selectedMassiveProcesses.length }}</strong> requerimientos masivos seleccionados.
                  </div>
                </div>
              </div>
              <div v-else class="d-flex align-center">
                <v-icon color="success" class="mr-3" large>mdi-check-decagram-outline</v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-bold success--text">
                    Asignación Simple
                  </div>
                  <div class="text-body-2 mb-0 grey--text text--darken-3">
                    Requerimiento activo: <span class="font-weight-bold">{{ activeMassiveProcessText }}</span>.
                    Usuarios asignados: <strong>{{ selectedMassiveUsers.length }}</strong>.
                  </div>
                </div>
              </div>
            </div>
            <div>
              <v-btn v-if="selectedMassiveProcesses.length > 1" color="orange darken-2" dark large :loading="savingMassiveAssignments"
                @click="saveMassiveAssignments">
                <v-icon left>mdi-content-save-all-outline</v-icon>
                Guardar Asignación Masiva
              </v-btn>
              <v-btn v-else color="success" large :loading="savingMassiveAssignments" :disabled="!selectedMassiveReqID"
                @click="saveMassiveAssignments">
                <v-icon left>mdi-content-save-outline</v-icon>
                Guardar Asignación
              </v-btn>
            </div>
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="!selectedMassiveReqID && !selectedMassiveUsrID && selectedMassiveProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">Resumen</div>
          <div class="text-body-2 grey--text">
            Haz clic en un requerimiento masivo para ver sus responsables asignados, o haz clic en un responsable para ver sus requerimientos asignados.
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="selectedMassiveReqID && selectedMassiveProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">
            Resumen de asignaciones para: {{ activeMassiveProcessText }}
          </div>
          <div v-if="massiveProcessUserList.length === 0" class="text-body-2 grey--text">
            No hay usuarios asignados a este requerimiento.
          </div>
          <div v-else class="d-flex flex-wrap">
            <v-chip v-for="user in massiveProcessUserList" :key="user.UsrID" small label class="ma-1" color="blue lighten-5"
              text-color="blue darken-3">
              <v-icon left small>mdi-account</v-icon>
              {{ user.UsrName }} - {{ user.FullName || user.NtpFullName }}
            </v-chip>
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4" v-if="selectedMassiveUsrID && !selectedMassiveReqID && selectedMassiveProcesses.length <= 1">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2">
            Requerimientos asignados a: {{ activeMassiveUserText }}
          </div>
          <div v-if="userMassiveProcessList.length === 0" class="text-body-2 grey--text">
            Este usuario no tiene requerimientos masivos asignados.
          </div>
          <div v-else class="d-flex flex-wrap">
            <v-chip v-for="req in userMassiveProcessList" :key="req.ReqID" small label class="ma-1" color="orange lighten-5"
              text-color="orange darken-4" @click="onMassiveProcessRowClick(req)">
              <v-icon left small>mdi-briefcase-outline</v-icon>
              REQ-{{ req.ReqID }} - {{ req.CmpName }} ({{ req.PstName }})
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <AlertSunshine :dialog="loadingScreen" />
  </v-container>
</template>

<script>
import _recruitmentAccessService from "@/services/HumanResource/RecruitmentAccessService";
import _recruitmentProcessService from "@/services/HumanResource/RecruitmentProcess/HmnRecruitmentProcessService";
import _userAssignmentService from "@/services/Security/UserAssignmentService";
import sUser from "../Security/UserOption/User.vue";

export default {
  name: "RecruitmentAccess",
  components: { sUser },
  data: () => ({
    activeTab: 0,
    loadingScreen: false,
    loadingProcesses: false,
    savingPermission: false,
    savingAssignments: false,
    permissionUser: {},
    isRecruitmentAdmin: false,
    processOptions: [],
    selectedRprID: null,
    selectedUsrID: null,
    userAssignedRprIDs: [],
    availableUsers: [],
    processUsers: [],
    processes: [],
    selectedProcesses: [],
    selectedUsers: [],
    searchProcess: "",
    searchUser: "",
    processHeaders: [
      { text: "ID", value: "RprID", width: "80px", align: "start" },
      { text: "Código", value: "ReqID", width: "120px" },
      { text: "Requerimiento / Puesto", value: "RequestedPosition" },
    ],
    userHeaders: [
      { text: "Usuario", value: "UsrName", width: "120px", align: "start" },
      { text: "Nombre Completo", value: "FullName" },
    ],

    // MASIVO PROPERTIES
    loadingMassiveProcesses: false,
    savingMassiveAssignments: false,
    massiveProcesses: [],
    selectedMassiveProcesses: [],
    selectedMassiveReqID: null,
    selectedMassiveUsrID: null,
    userAssignedMassiveReqIDs: [],
    selectedMassiveUsers: [],
    searchMassiveProcess: "",
    searchMassiveUser: "",
    massiveProcessHeaders: [
      { text: "ID", value: "ReqID", width: "100px", align: "start" },
      { text: "Campaña", value: "CmpName" },
      { text: "Cargo / Puesto", value: "PstName" },
      { text: "Vacantes", value: "ReqQuantity", width: "120px" },
    ],
  }),
  computed: {
    processUserList() {
      return this.selectedUsers || [];
    },
    userProcessList() {
      if (!this.selectedUsrID || !this.userAssignedRprIDs.length) return [];
      return (this.processes || []).filter((p) => this.userAssignedRprIDs.includes(p.RprID));
    },
    activeProcessText() {
      if (!this.selectedRprID) return "";
      const proc = (this.processes || []).find((p) => p.RprID === this.selectedRprID);
      if (proc) {
        return `${proc.RprID} - ${proc.ReqName || proc.RequestedPosition || "Proceso"}`;
      }
      return `Proceso ${this.selectedRprID}`;
    },
    activeUserText() {
      if (!this.selectedUsrID) return "";
      const user = (this.availableUsers || []).find((u) => u.UsrID === this.selectedUsrID);
      if (user) {
        return `${user.UsrName} - ${user.FullName || user.NtpFullName || ""}`;
      }
      return `Usuario ${this.selectedUsrID}`;
    },

    // MASIVO COMPUTED
    massiveProcessUserList() {
      return this.selectedMassiveUsers || [];
    },
    userMassiveProcessList() {
      if (!this.selectedMassiveUsrID || !this.userAssignedMassiveReqIDs.length) return [];
      return (this.massiveProcesses || []).filter((p) => this.userAssignedMassiveReqIDs.includes(p.ReqID));
    },
    activeMassiveProcessText() {
      if (!this.selectedMassiveReqID) return "";
      const req = (this.massiveProcesses || []).find((p) => p.ReqID === this.selectedMassiveReqID);
      if (req) {
        return `REQ-${req.ReqID} - ${req.CmpName} (${req.PstName})`;
      }
      return `Requerimiento ${this.selectedMassiveReqID}`;
    },
    activeMassiveUserText() {
      if (!this.selectedMassiveUsrID) return "";
      const user = (this.availableUsers || []).find((u) => u.UsrID === this.selectedMassiveUsrID);
      if (user) {
        return `${user.UsrName} - ${user.FullName || user.NtpFullName || ""}`;
      }
      return `Usuario ${this.selectedMassiveUsrID}`;
    },
  },
  watch: {
    selectedProcesses(newVal) {
      if (!newVal || newVal.length === 0) {
        this.selectedRprID = null;
        this.selectedUsers = [];
      } else if (newVal.length === 1) {
        const singleProcess = newVal[0];
        if (this.selectedRprID !== singleProcess.RprID) {
          this.selectedUsrID = null;
          this.userAssignedRprIDs = [];
          this.selectedRprID = singleProcess.RprID;
          this.onProcessSelected(singleProcess.RprID);
        }
      } else {
        this.selectedRprID = null;
        this.selectedUsrID = null;
        this.userAssignedRprIDs = [];
      }
    },
    selectedMassiveProcesses(newVal) {
      if (!newVal || newVal.length === 0) {
        this.selectedMassiveReqID = null;
        this.selectedMassiveUsers = [];
      } else if (newVal.length === 1) {
        const singleReq = newVal[0];
        if (this.selectedMassiveReqID !== singleReq.ReqID) {
          this.selectedMassiveUsrID = null;
          this.userAssignedMassiveReqIDs = [];
          this.selectedMassiveReqID = singleReq.ReqID;
          this.onMassiveProcessSelected(singleReq.ReqID);
        }
      } else {
        this.selectedMassiveReqID = null;
        this.selectedMassiveUsrID = null;
        this.userAssignedMassiveReqIDs = [];
      }
    },
    activeTab(newVal) {
      // Limpiar selecciones al cambiar de tab
      this.selectedRprID = null;
      this.selectedUsrID = null;
      this.selectedProcesses = [];
      this.selectedUsers = [];
      this.userAssignedRprIDs = [];

      this.selectedMassiveReqID = null;
      this.selectedMassiveUsrID = null;
      this.selectedMassiveProcesses = [];
      this.selectedMassiveUsers = [];
      this.userAssignedMassiveReqIDs = [];

      if (newVal === 1 && this.massiveProcesses.length === 0) {
        this.loadMassiveApproved();
      }
    }
  },
  mounted() {
    this.loadAssignableUsers();
    this.loadProcesses();
  },
  methods: {
    extractSingleUser(payload) {
      if (!payload) return null;
      if (payload.UsrID) return payload;
      if (Array.isArray(payload)) {
        return payload.find((item) => item && item.UsrID) || null;
      }
      if (typeof payload === "object") {
        const values = Object.keys(payload).map((key) => payload[key]);
        return values.find((item) => item && item.UsrID) || null;
      }
      return null;
    },
    onPermissionUserSelected(user) {
      const selectedUser = this.extractSingleUser(user);
      this.permissionUser = selectedUser || {};

      if (!selectedUser) {
        this.isRecruitmentAdmin = false;
        return;
      }

      this.loadingScreen = true;
      _recruitmentAccessService
        .permission(selectedUser.UsrID, this.$fun.getUserID())
        .then((response) => {
          const data = response && response.data ? response.data : null;
          this.isRecruitmentAdmin = !!(data && (data.IsAdmin === 1 || data.IsAdmin === true));
        })
        .finally(() => {
          this.loadingScreen = false;
        });
    },
    savePermission() {
      if (!this.permissionUser || !this.permissionUser.UsrID) return;

      this.$fun.alert("¿Guardar permiso de reclutamiento?", "question").then((r) => {
        if (!r.value) return;

        this.savingPermission = true;
        _recruitmentAccessService
          .savePermission(
            {
              UsrID: this.permissionUser.UsrID,
              IsAdmin: this.isRecruitmentAdmin,
            },
            this.$fun.getUserID()
          )
          .then(() => {
            this.$fun.alert("Permiso actualizado correctamente.", "success");
          })
          .finally(() => {
            this.savingPermission = false;
          });
      });
    },
    loadAssignableUsers() {
      _userAssignmentService.list(this.$fun.getUserID(), this.$fun.getUserID())
        .then((response) => {
          const rows = response && response.data ? response.data : [];
          this.availableUsers = Object.assign([], rows).filter((item) => item && item.UsrID);
        })
        .catch(() => {
          this.availableUsers = [];
        });
    },
    loadProcesses() {
      this.loadingProcesses = true;
      const parameters = {
        draw: 1,
        start: 0,
        length: 500,
        searchText: "",
        filter: {
          ModuleType: 1,
          AllProcesses: true,
        },
      };

      _recruitmentProcessService
        .pagination(parameters, this.$fun.getUserID())
        .then((response) => {
          const rows = response && response.data && response.data.data ? response.data.data : [];
          this.processes = rows;
          this.processOptions = rows.map((item) => ({
            text: `${item.RprID} - ${item.ReqName || item.RequestedPosition || "Proceso"}`,
            value: item.RprID,
            raw: item,
          }));
        })
        .finally(() => {
          this.loadingProcesses = false;
        });
    },
    reloadProcesses() {
      this.loadProcesses();
    },
    onProcessSelected(rprID) {
      if (!rprID) {
        this.processUsers = [];
        this.selectedUsers = [];
        return;
      }

      this.loadingScreen = true;
      _recruitmentAccessService
        .assignments(rprID, this.$fun.getUserID())
        .then((response) => {
          const rows = response && response.data ? response.data : [];
          this.processUsers = Object.assign([], rows);
          const assignedIDs = new Set(
            rows.map((u) => u.UsrID || u.AssignedUsrID || 0).filter((id) => id > 0)
          );
          this.selectedUsers = (this.availableUsers || []).filter((u) => assignedIDs.has(u.UsrID));
        })
        .finally(() => {
          this.loadingScreen = false;
        });
    },
    onProcessRowClick(row) {
      this.selectedUsrID = null;
      this.userAssignedRprIDs = [];
      this.selectedRprID = row.RprID;
      this.selectedProcesses = [row];
      this.onProcessSelected(row.RprID);
    },
    getProcessRowClass(item) {
      if (item.RprID === this.selectedRprID) {
        return "active-row-highlight";
      }
      if (this.selectedUsrID && (this.userAssignedRprIDs || []).includes(item.RprID)) {
        return "assigned-to-user-highlight";
      }
      return "";
    },
    onUserRowClick(row) {
      this.selectedUsrID = row.UsrID;
      this.selectedRprID = null;
      this.selectedProcesses = [];
      this.onUserSelected(row.UsrID);
    },
    getUserRowClass(item) {
      return item.UsrID === this.selectedUsrID ? "active-row-highlight" : "";
    },
    onUserSelected(usrID) {
      if (!usrID) {
        this.userAssignedRprIDs = [];
        return;
      }
      this.loadingScreen = true;
      _recruitmentAccessService
        .assignmentsByUser(usrID, this.$fun.getUserID())
        .then((response) => {
          this.userAssignedRprIDs = response && response.data ? response.data : [];
        })
        .finally(() => {
          this.loadingScreen = false;
        });
    },
    saveAssignments() {
      let rprIDs = [];
      if (this.selectedProcesses && this.selectedProcesses.length > 1) {
        rprIDs = this.selectedProcesses.map((p) => p.RprID);
      } else if (this.selectedRprID) {
        rprIDs = [this.selectedRprID];
      }

      if (rprIDs.length === 0) {
        this.$fun.alert("Por favor, selecciona al menos un proceso.", "warning");
        return;
      }

      const allowedIDs = new Set((this.availableUsers || []).map((item) => item.UsrID));
      const sanitizedUserIDs = (this.selectedUsers || []).map((u) => u.UsrID).filter((id) => allowedIDs.has(id));

      const message = rprIDs.length > 1
        ? `¿Seguro de asignar de manera masiva los ${sanitizedUserIDs.length} responsables seleccionados a los ${rprIDs.length} procesos profesionales seleccionados?`
        : `¿Guardar usuarios asignados al proceso seleccionado?`;

      this.$fun.alert(message, "question").then((r) => {
        if (!r.value) return;

        this.savingAssignments = true;

        if (rprIDs.length === 1) {
          const payload = {
            RprID: rprIDs[0],
            UsrIDs: sanitizedUserIDs,
          };
          _recruitmentAccessService
            .saveAssignments(payload, this.$fun.getUserID())
            .then(() => {
              this.$fun.alert("Asignaciones guardadas correctamente.", "success");
              this.onProcessSelected(rprIDs[0]);
            })
            .finally(() => {
              this.savingAssignments = false;
            });
        } else {
          const promises = rprIDs.map((rprID) => {
            const payload = {
              RprID: rprID,
              UsrIDs: sanitizedUserIDs,
            };
            return _recruitmentAccessService.saveAssignments(payload, this.$fun.getUserID());
          });

          Promise.all(promises)
            .then(() => {
              this.$fun.alert("Asignación masiva guardada correctamente.", "success");
              this.selectedProcesses = [];
              this.selectedUsers = [];
              this.selectedRprID = null;
              this.loadProcesses();
            })
            .catch((error) => {
              console.error("Error al realizar asignación masiva:", error);
              this.$fun.alert("Ocurrió un error en alguna de las asignaciones masivas.", "error");
            })
            .finally(() => {
              this.savingAssignments = false;
            });
        }
      });
    },

    // MASIVO METHODS
    loadMassiveApproved() {
      this.loadingMassiveProcesses = true;
      _recruitmentAccessService
        .massiveApprovedRequirements(this.$fun.getUserID())
        .then((response) => {
          this.massiveProcesses = response && response.data ? response.data : [];
        })
        .finally(() => {
          this.loadingMassiveProcesses = false;
        });
    },
    onMassiveProcessSelected(reqID) {
      if (!reqID) {
        this.selectedMassiveUsers = [];
        return;
      }

      this.loadingScreen = true;
      _recruitmentAccessService
        .massiveAssignments(reqID, this.$fun.getUserID())
        .then((response) => {
          const rows = response && response.data ? response.data : [];
          const assignedIDs = new Set(
            rows.map((u) => u.UsrID || u.AssignedUsrID || 0).filter((id) => id > 0)
          );
          this.selectedMassiveUsers = (this.availableUsers || []).filter((u) => assignedIDs.has(u.UsrID));
        })
        .finally(() => {
          this.loadingScreen = false;
        });
    },
    onMassiveProcessRowClick(row) {
      this.selectedMassiveUsrID = null;
      this.userAssignedMassiveReqIDs = [];
      this.selectedMassiveReqID = row.ReqID;
      this.selectedMassiveProcesses = [row];
      this.onMassiveProcessSelected(row.ReqID);
    },
    getMassiveRowClass(item) {
      if (item.ReqID === this.selectedMassiveReqID) {
        return "active-row-highlight";
      }
      if (this.selectedMassiveUsrID && (this.userAssignedMassiveReqIDs || []).includes(item.ReqID)) {
        return "assigned-to-user-highlight";
      }
      return "";
    },
    onMassiveUserRowClick(row) {
      this.selectedMassiveUsrID = row.UsrID;
      this.selectedMassiveReqID = null;
      this.selectedMassiveProcesses = [];
      this.onMassiveUserSelected(row.UsrID);
    },
    getMassiveUserRowClass(item) {
      return item.UsrID === this.selectedMassiveUsrID ? "active-row-highlight" : "";
    },
    onMassiveUserSelected(usrID) {
      if (!usrID) {
        this.userAssignedMassiveReqIDs = [];
        return;
      }
      this.loadingScreen = true;
      _recruitmentAccessService
        .massiveAssignmentsByUser(usrID, this.$fun.getUserID())
        .then((response) => {
          this.userAssignedMassiveReqIDs = response && response.data ? response.data : [];
        })
        .finally(() => {
          this.loadingScreen = false;
        });
    },
    saveMassiveAssignments() {
      let reqIDs = [];
      if (this.selectedMassiveProcesses && this.selectedMassiveProcesses.length > 1) {
        reqIDs = this.selectedMassiveProcesses.map((p) => p.ReqID);
      } else if (this.selectedMassiveReqID) {
        reqIDs = [this.selectedMassiveReqID];
      }

      if (reqIDs.length === 0) {
        this.$fun.alert("Por favor, selecciona al menos un requerimiento masivo.", "warning");
        return;
      }

      const allowedIDs = new Set((this.availableUsers || []).map((item) => item.UsrID));
      const sanitizedUserIDs = (this.selectedMassiveUsers || []).map((u) => u.UsrID).filter((id) => allowedIDs.has(id));

      const message = reqIDs.length > 1
        ? `¿Seguro de asignar de manera masiva los ${sanitizedUserIDs.length} responsables seleccionados a los ${reqIDs.length} requerimientos masivos seleccionados?`
        : `¿Guardar usuarios asignados al requerimiento masivo seleccionado?`;

      this.$fun.alert(message, "question").then((r) => {
        if (!r.value) return;

        this.savingMassiveAssignments = true;

        if (reqIDs.length === 1) {
          const payload = {
            ReqID: reqIDs[0],
            UsrIDs: sanitizedUserIDs,
          };
          _recruitmentAccessService
            .saveMassiveAssignments(payload, this.$fun.getUserID())
            .then(() => {
              this.$fun.alert("Asignaciones guardadas correctamente.", "success");
              this.onMassiveProcessSelected(reqIDs[0]);
            })
            .finally(() => {
              this.savingMassiveAssignments = false;
            });
        } else {
          const promises = reqIDs.map((reqID) => {
            const payload = {
              ReqID: reqID,
              UsrIDs: sanitizedUserIDs,
            };
            return _recruitmentAccessService.saveMassiveAssignments(payload, this.$fun.getUserID());
          });

          Promise.all(promises)
            .then(() => {
              this.$fun.alert("Asignación masiva guardada correctamente.", "success");
              this.selectedMassiveProcesses = [];
              this.selectedMassiveUsers = [];
              this.selectedMassiveReqID = null;
              this.loadMassiveApproved();
            })
            .catch((error) => {
              console.error("Error al realizar asignación masiva:", error);
              this.$fun.alert("Ocurrió un error en alguna de las asignaciones masivas.", "error");
            })
            .finally(() => {
              this.savingMassiveAssignments = false;
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.recruitment-access-page {
  background: linear-gradient(180deg, #f8fafc 0%, #eef4f8 100%);
  min-height: 100%;
}

.assignment-hero {
  background: linear-gradient(135deg, #4b2a16 0%, #a65c2c 100%);
}

.assignment-subtitle {
  color: rgba(255, 255, 255, 0.85);
  max-width: 820px;
}

.recruitment-card,
.user-card {
  border-radius: 12px;
  background-color: #ffffff;
}

::v-deep .active-row-highlight {
  background-color: #f0f9ff !important;
}

::v-deep .active-row-highlight td {
  font-weight: 600;
  color: #0284c7 !important;
}

::v-deep .assigned-to-user-highlight {
  background-color: #fff7ed !important;
}

::v-deep .assigned-to-user-highlight td {
  font-weight: 600;
  color: #ea580c !important;
}

::v-deep .recruitment-table tbody tr,
::v-deep .user-table tbody tr {
  cursor: pointer;
}

::v-deep .recruitment-table .v-data-table__wrapper,
::v-deep .user-table .v-data-table__wrapper {
  max-height: 480px;
  overflow-y: auto;
}

.assignment-status-card {
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
